import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Search, Plus, Trash2, X, UserPlus, Mail, MapPin, Phone, MessageSquare,
  Coins, Calendar, Tag, Send, CheckCircle, Edit3, Save, Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCIES = ['NOK', 'USD', 'GBP', 'EUR'];

// All "where this person sits in your relationship" options. Mirrors the
// backend CONVERSATION_STATUSES but spelled out for humans.
const CRM_STATUS_OPTIONS = [
  { value: 'initial_contact',  label: 'Initial contact',   color: 'bg-slate-100 text-slate-700' },
  { value: 'in_discussion',    label: 'In discussion',     color: 'bg-sky-100 text-sky-700' },
  { value: 'pledge_committed', label: 'Pledge committed',  color: 'bg-emerald-100 text-emerald-700' },
  { value: 'declined',         label: 'Declined',          color: 'bg-rose-100 text-rose-700' },
  { value: 'on_hold',          label: 'On hold',           color: 'bg-amber-100 text-amber-700' },
  { value: 'followup_needed',  label: 'Follow-up needed',  color: 'bg-purple-100 text-purple-700' },
];

const PLEDGE_TYPE_OPTIONS = [
  { value: '',           label: '— None —' },
  { value: 'monthly',    label: 'Monthly' },
  { value: 'one_time',   label: 'One-time' },
  { value: 'prayer_only', label: 'Prayer only' },
];

const PLEDGE_STATUS_OPTIONS = [
  { value: 'pledged',  label: 'Pledged' },
  { value: 'active',   label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const ORIGIN_BADGES = {
  app_signup: { label: 'App signup',  color: 'bg-blue-100 text-blue-700' },
  manual:     { label: 'Manual',      color: 'bg-slate-200 text-slate-700' },
};

const Empty = ({ children }) => <span className="text-slate-400">{children}</span>;

const fmtAmount = (p) => {
  const amt = Number(p.pledge_amount || 0);
  if (!amt) return <Empty>—</Empty>;
  return `${p.pledge_currency || 'NOK'} ${amt.toLocaleString()}`;
};

const fmtDate = (s) => {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return s; }
};

// ============================================================================
// Drawer
// ============================================================================
const PersonDrawer = ({ person, onClose, onSaved, onDeleted, onMessage }) => {
  const [form, setForm]   = useState(person);
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  useEffect(() => { setForm(person); setNewNote(''); }, [person]);

  if (!person) return null;

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        country: form.country,
        whatsapp: form.whatsapp,
        pledge_type: form.pledge_type || null,
        pledge_amount: Number(form.pledge_amount || 0),
        pledge_currency: form.pledge_currency || 'NOK',
        pledge_status: form.pledge_status || null,
        pledge_start: form.pledge_start || '',
        pledge_source: form.pledge_source || '',
        crm_status: form.crm_status || 'initial_contact',
        newsletter_subscribed: !!form.newsletter_subscribed,
        admin_message: form.admin_message || '',
      };
      const { data } = await axios.put(
        `${API}/admin/people/${person.id}`,
        payload,
        { withCredentials: true }
      );
      onSaved?.(data);
      onMessage?.('Person updated');
    } catch (e) {
      onMessage?.(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/people/${person.id}/notes`,
        { text: newNote.trim() },
        { withCredentials: true }
      );
      onSaved?.(data);
      setNewNote('');
      onMessage?.('Note added');
    } catch (e) {
      onMessage?.(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setAddingNote(false);
    }
  };

  const deleteNote = async (idx) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/admin/people/${person.id}/notes/${idx}`, { withCredentials: true });
      // Re-fetch the person to get the updated notes_log
      const { data } = await axios.get(`${API}/admin/people`, { withCredentials: true });
      const fresh = (data || []).find((p) => p.id === person.id);
      if (fresh) onSaved?.(fresh);
      onMessage?.('Note deleted');
    } catch (e) {
      onMessage?.(`Failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const deletePerson = async () => {
    const warn = person.origin === 'app_signup'
      ? `Delete ${person.name}? This will also remove their account login. They will be locked out.`
      : `Delete ${person.name}?`;
    if (!window.confirm(warn)) return;
    try {
      await axios.delete(`${API}/admin/people/${person.id}`, { withCredentials: true });
      onDeleted?.(person.id);
      onMessage?.('Person deleted');
      onClose();
    } catch (e) {
      onMessage?.(`Failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const originBadge = ORIGIN_BADGES[person.origin] || ORIGIN_BADGES.manual;

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="person-drawer-backdrop"
        className="fixed inset-0 bg-slate-900/50 z-40 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Drawer */}
      <aside
        data-testid="person-drawer"
        className="fixed top-0 right-0 bottom-0 w-full sm:w-[560px] bg-white shadow-2xl z-50 overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold flex-shrink-0">
              {(person.name || '?').trim().charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-slate-900 truncate font-['Outfit']">{person.name || '(unnamed)'}</h3>
              <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${originBadge.color}`}>
                {originBadge.label}
              </span>
            </div>
          </div>
          <button
            data-testid="person-drawer-close"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Contact */}
          <section>
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Contact</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-slate-600 mb-1 block">Name</span>
                <input
                  data-testid="drawer-name"
                  type="text"
                  value={form.name || ''}
                  onChange={(e) => update({ name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </label>
              <label className="block">
                <span className="text-xs text-slate-600 mb-1 block">Email</span>
                <input
                  data-testid="drawer-email"
                  type="email"
                  value={form.email || ''}
                  onChange={(e) => update({ email: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </label>
              <label className="block">
                <span className="text-xs text-slate-600 mb-1 block">Country</span>
                <input
                  data-testid="drawer-country"
                  type="text"
                  value={form.country || ''}
                  onChange={(e) => update({ country: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </label>
              <label className="block">
                <span className="text-xs text-slate-600 mb-1 block">WhatsApp / Phone</span>
                <input
                  data-testid="drawer-whatsapp"
                  type="text"
                  value={form.whatsapp || ''}
                  onChange={(e) => update({ whatsapp: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </label>
            </div>
            <label className="flex items-center gap-2 mt-3">
              <input
                data-testid="drawer-newsletter"
                type="checkbox"
                checked={!!form.newsletter_subscribed}
                onChange={(e) => update({ newsletter_subscribed: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span className="text-sm text-slate-700">Subscribed to newsletter</span>
            </label>
          </section>

          {/* Pledge */}
          <section className="border-t border-slate-100 pt-5">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Pledge</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-slate-600 mb-1 block">Pledge type</span>
                <select
                  data-testid="drawer-pledge-type"
                  value={form.pledge_type || ''}
                  onChange={(e) => update({ pledge_type: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
                >
                  {PLEDGE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-slate-600 mb-1 block">Status</span>
                <select
                  data-testid="drawer-pledge-status"
                  value={form.pledge_status || 'pledged'}
                  onChange={(e) => update({ pledge_status: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
                >
                  {PLEDGE_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-slate-600 mb-1 block">Amount</span>
                <div className="flex gap-2">
                  <input
                    data-testid="drawer-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.pledge_amount ?? 0}
                    onChange={(e) => update({ pledge_amount: parseFloat(e.target.value) || 0 })}
                    className="flex-1 px-3 py-2 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    data-testid="drawer-currency"
                    value={form.pledge_currency || 'NOK'}
                    onChange={(e) => update({ pledge_currency: e.target.value })}
                    className="w-24 px-2 py-2 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </label>
              <label className="block">
                <span className="text-xs text-slate-600 mb-1 block">Start date</span>
                <input
                  data-testid="drawer-start"
                  type="text"
                  placeholder="e.g. April 2027"
                  value={form.pledge_start || ''}
                  onChange={(e) => update({ pledge_start: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs text-slate-600 mb-1 block">Pledge source (e.g. Wycliffe Norway)</span>
                <input
                  data-testid="drawer-source"
                  type="text"
                  value={form.pledge_source || ''}
                  onChange={(e) => update({ pledge_source: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </label>
            </div>
          </section>

          {/* CRM */}
          <section className="border-t border-slate-100 pt-5">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">CRM</h4>
            <label className="block">
              <span className="text-xs text-slate-600 mb-1 block">Where they sit in the relationship</span>
              <select
                data-testid="drawer-crm-status"
                value={form.crm_status || 'initial_contact'}
                onChange={(e) => update({ crm_status: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
              >
                {CRM_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="block mt-3">
              <span className="text-xs text-slate-600 mb-1 block">Admin note / message (private)</span>
              <textarea
                data-testid="drawer-admin-message"
                rows="3"
                value={form.admin_message || ''}
                onChange={(e) => update({ admin_message: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
              />
            </label>
          </section>

          {/* Notes log */}
          <section className="border-t border-slate-100 pt-5">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
              Conversation log ({(form.notes_log || []).length})
            </h4>

            <div className="flex gap-2 mb-3">
              <input
                data-testid="person-note-input drawer-new-note"
                type="text"
                placeholder="Add a note — date/time stamp is added automatically"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addNote(); } }}
                className="flex-1 px-3 py-2 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
              />
              <button
                data-testid="person-note-add drawer-add-note-btn"
                onClick={addNote}
                disabled={addingNote || !newNote.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                Add
              </button>
            </div>

            {(form.notes_log || []).length === 0 ? (
              <p className="text-sm text-slate-400 italic">No notes yet.</p>
            ) : (
              <ul className="space-y-2 max-h-64 overflow-y-auto">
                {(form.notes_log || []).slice().reverse().map((n, ri) => {
                  // Reversed display, but delete API uses original index
                  const original = (form.notes_log.length - 1) - ri;
                  return (
                    <li key={original} className="bg-slate-50 border border-slate-100 rounded-lg p-3 group">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm text-slate-700 whitespace-pre-wrap flex-1">{n.text}</p>
                        <button
                          data-testid={`drawer-delete-note-${original}`}
                          onClick={() => deleteNote(original)}
                          className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600 transition-opacity"
                          title="Delete note"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <span className="text-[11px] text-slate-400 block mt-1">{fmtDate(n.timestamp)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        {/* Sticky footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex justify-between items-center">
          <button
            data-testid="drawer-delete-person"
            onClick={deletePerson}
            className="flex items-center gap-2 text-rose-600 hover:text-rose-700 text-sm font-medium"
          >
            <Trash2 className="w-4 h-4" /> Delete
          </button>
          <button
            data-testid="drawer-save-btn"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 shadow-sm disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </aside>
    </>
  );
};

// ============================================================================
// Add-person modal
// ============================================================================
const AddPersonModal = ({ onClose, onAdded, onMessage }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    country: '',
    whatsapp: '',
    pledge_type: '',
    pledge_amount: 0,
    pledge_currency: 'NOK',
    pledge_status: 'pledged',
    pledge_start: '',
    pledge_source: '',
    crm_status: 'initial_contact',
    newsletter_subscribed: false,
    admin_message: '',
    initial_note: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/people`, form, { withCredentials: true });
      onAdded?.(data);
      onMessage?.('Person added');
      onClose();
    } catch (err) {
      onMessage?.(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div data-testid="add-person-modal" className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex justify-between items-center">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2"><UserPlus className="w-5 h-5" /> Add person</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          <input data-testid="add-person-name"
            type="text" required placeholder="Name *"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2.5 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
          />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="add-person-email"
              type="email" placeholder="Email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="px-3 py-2.5 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
            />
            <input data-testid="add-person-country"
              type="text" placeholder="Country"
              value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
              className="px-3 py-2.5 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <input data-testid="add-person-whatsapp"
            type="text" placeholder="WhatsApp / phone"
            value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
            className="w-full px-3 py-2.5 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
          />
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="add-person-pledge-type"
              value={form.pledge_type}
              onChange={(e) => setForm({ ...form, pledge_type: e.target.value })}
              className="px-3 py-2.5 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500">
              {PLEDGE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select data-testid="add-person-crm-status"
              value={form.crm_status}
              onChange={(e) => setForm({ ...form, crm_status: e.target.value })}
              className="px-3 py-2.5 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500">
              {CRM_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <input data-testid="add-person-amount"
              type="number" placeholder="Amount" min="0"
              value={form.pledge_amount || ''}
              onChange={(e) => setForm({ ...form, pledge_amount: parseFloat(e.target.value) || 0 })}
              className="px-3 py-2.5 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
            />
            <select data-testid="add-person-currency"
              value={form.pledge_currency}
              onChange={(e) => setForm({ ...form, pledge_currency: e.target.value })}
              className="px-3 py-2.5 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500">
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input data-testid="add-person-start"
              type="text" placeholder="Start (Apr 2027)"
              value={form.pledge_start}
              onChange={(e) => setForm({ ...form, pledge_start: e.target.value })}
              className="px-3 py-2.5 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <input data-testid="add-person-source"
            type="text" placeholder="Pledge source (e.g. Wycliffe Norway)"
            value={form.pledge_source}
            onChange={(e) => setForm({ ...form, pledge_source: e.target.value })}
            className="w-full px-3 py-2.5 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
          />
          <textarea data-testid="add-person-note"
            rows="3" placeholder="Initial note / conversation (optional)"
            value={form.initial_note}
            onChange={(e) => setForm({ ...form, initial_note: e.target.value })}
            className="w-full px-3 py-2.5 rounded-lg border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
          />
          <label className="flex items-center gap-2 pt-1">
            <input type="checkbox" checked={form.newsletter_subscribed}
              onChange={(e) => setForm({ ...form, newsletter_subscribed: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-sm text-slate-700">Subscribe to newsletter</span>
          </label>
          <div className="flex justify-end gap-2 pt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600 hover:text-slate-900">Cancel</button>
            <button
              data-testid="add-person-submit"
              type="submit" disabled={saving}
              className="px-5 py-2 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Adding…' : 'Add person'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// Main PeopleTab
// ============================================================================
export const PeopleTab = ({ onMessage }) => {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterOrigin, setFilterOrigin] = useState('all');     // all | app_signup | manual
  const [filterPledge, setFilterPledge] = useState('all');     // all | monthly | one_time | prayer_only | none
  const [filterCrm,    setFilterCrm]    = useState('all');     // all | <crm status>
  const [active, setActive] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const fetchPeople = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/people`, { withCredentials: true });
      setPeople(data || []);
    } catch (e) {
      onMessage?.(`Load failed: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchPeople(); }, []);

  // Counts (run on full list, not filtered)
  const counts = useMemo(() => {
    return {
      total:        people.length,
      app:          people.filter((p) => p.origin === 'app_signup').length,
      manual:       people.filter((p) => p.origin === 'manual').length,
      monthly:      people.filter((p) => p.pledge_type === 'monthly').length,
      one_time:     people.filter((p) => p.pledge_type === 'one_time').length,
      prayer_only:  people.filter((p) => p.pledge_type === 'prayer_only').length,
      activeMon:    people.filter((p) => p.pledge_type === 'monthly' && p.pledge_status === 'active').length,
    };
  }, [people]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return people.filter((p) => {
      if (filterOrigin !== 'all' && p.origin !== filterOrigin) return false;
      if (filterPledge !== 'all') {
        if (filterPledge === 'none' && p.pledge_type) return false;
        if (filterPledge !== 'none' && p.pledge_type !== filterPledge) return false;
      }
      if (filterCrm !== 'all' && p.crm_status !== filterCrm) return false;
      if (!q) return true;
      return (
        (p.name || '').toLowerCase().includes(q) ||
        (p.email || '').toLowerCase().includes(q) ||
        (p.country || '').toLowerCase().includes(q) ||
        (p.whatsapp || '').toLowerCase().includes(q) ||
        (p.admin_message || '').toLowerCase().includes(q) ||
        (p.pledge_source || '').toLowerCase().includes(q)
      );
    });
  }, [people, search, filterOrigin, filterPledge, filterCrm]);

  const handleSaved = (updated) => {
    setPeople((arr) => arr.map((p) => (p.id === updated.id ? updated : p)));
    setActive(updated);
  };
  const handleAdded = (created) => {
    setPeople((arr) => [created, ...arr]);
  };
  const handleDeleted = (id) => {
    setPeople((arr) => arr.filter((p) => p.id !== id));
  };

  return (
    <div data-testid="admin-people-tab" className="space-y-5">
      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div data-testid="people-stat-total" className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Total people</p>
          <p className="text-3xl font-bold text-slate-900">{counts.total}</p>
        </div>
        <div data-testid="people-stat-active" className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Active monthly</p>
          <p className="text-3xl font-bold text-emerald-600">{counts.activeMon}</p>
        </div>
        <div data-testid="people-stat-monthly-pledges" className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Monthly pledges</p>
          <p className="text-3xl font-bold text-amber-600">{counts.monthly}</p>
        </div>
        <div data-testid="people-stat-prayer" className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Prayer / other</p>
          <p className="text-3xl font-bold text-purple-600">{counts.prayer_only}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="people-search-input people-search"
              type="text"
              placeholder="Search name, email, country, notes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            data-testid="people-add-button"
            onClick={() => setShowAdd(true)}
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 shadow-sm whitespace-nowrap"
          >
            <Plus className="w-4 h-4" /> Add person
          </button>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mt-4">
          {/* Origin */}
          {[
            { v: 'all',         l: `All (${counts.total})` },
            { v: 'app_signup',  l: `App signup (${counts.app})` },
            { v: 'manual',      l: `Manual (${counts.manual})` },
          ].map((c) => (
            <button
              key={c.v}
              data-testid={`people-filter-origin-${c.v}`}
              onClick={() => setFilterOrigin(c.v)}
              className={`px-3 py-1 text-xs rounded-full border ${
                filterOrigin === c.v ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >{c.l}</button>
          ))}
          <span className="w-px bg-slate-200 mx-1" />
          {/* Pledge type */}
          {[
            { v: 'all',         l: 'All pledge types' },
            { v: 'monthly',     l: `Monthly (${counts.monthly})` },
            { v: 'one_time',    l: `One-time (${counts.one_time})` },
            { v: 'prayer_only', l: `Prayer (${counts.prayer_only})` },
            { v: 'none',        l: 'No pledge' },
          ].map((c) => (
            <button
              key={c.v}
              data-testid={`people-filter-pledge-${c.v}`}
              onClick={() => setFilterPledge(c.v)}
              className={`px-3 py-1 text-xs rounded-full border ${
                filterPledge === c.v ? 'bg-amber-500 text-slate-900 border-amber-500' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >{c.l}</button>
          ))}
          <span className="w-px bg-slate-200 mx-1" />
          {/* CRM */}
          <select
            data-testid="people-filter-crm"
            value={filterCrm}
            onChange={(e) => setFilterCrm(e.target.value)}
            className="px-3 py-1 text-xs rounded-full border border-slate-200 bg-white text-slate-700"
          >
            <option value="all">All CRM stages</option>
            {CRM_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No people match your filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="people-table" className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold text-slate-700">Name</th>
                  <th className="px-4 py-3 font-semibold text-slate-700 hidden md:table-cell">Contact</th>
                  <th className="px-4 py-3 font-semibold text-slate-700">Pledge</th>
                  <th className="px-4 py-3 font-semibold text-slate-700 hidden lg:table-cell">CRM</th>
                  <th className="px-4 py-3 font-semibold text-slate-700 hidden lg:table-cell">Origin</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const origin = ORIGIN_BADGES[p.origin] || ORIGIN_BADGES.manual;
                  const crm = CRM_STATUS_OPTIONS.find((c) => c.value === p.crm_status) || CRM_STATUS_OPTIONS[0];
                  return (
                    <tr
                      key={p.id}
                      data-testid={`people-row-${p.id}`}
                      onClick={() => setActive(p)}
                      className="border-t border-slate-100 cursor-pointer hover:bg-blue-50/40 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold flex-shrink-0">
                            {(p.name || '?').trim().charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 truncate">{p.name || '(unnamed)'}</p>
                            <p className="text-xs text-slate-500 truncate md:hidden">{p.email || p.whatsapp || p.country || ''}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 hidden md:table-cell">
                        <div className="space-y-0.5">
                          {p.email && <div className="flex items-center gap-1.5 text-xs"><Mail className="w-3 h-3" />{p.email}</div>}
                          {p.country && <div className="flex items-center gap-1.5 text-xs"><MapPin className="w-3 h-3" />{p.country}</div>}
                          {p.whatsapp && <div className="flex items-center gap-1.5 text-xs"><Phone className="w-3 h-3" />{p.whatsapp}</div>}
                          {!p.email && !p.country && !p.whatsapp && <Empty>—</Empty>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-slate-900 font-semibold">{fmtAmount(p)}</div>
                        {p.pledge_type && (
                          <div className="text-xs text-slate-500 capitalize">
                            {p.pledge_type.replace('_', ' ')}{p.pledge_status ? ` · ${p.pledge_status}` : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${crm.color}`}>
                          {crm.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${origin.color}`}>
                          {origin.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Drawer */}
      {active && (
        <PersonDrawer
          person={active}
          onClose={() => setActive(null)}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
          onMessage={onMessage}
        />
      )}

      {/* Add modal */}
      {showAdd && (
        <AddPersonModal
          onClose={() => setShowAdd(false)}
          onAdded={handleAdded}
          onMessage={onMessage}
        />
      )}
    </div>
  );
};
